import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, forkJoin, startWith } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { PrimaryButton } from '@components/button/primary-button/primary-button';
import { SecondaryButton } from '@components/button/secondary-button/secondary-button';
import {
  DataTableComponent,
  TableColumn,
  TableConfig,
} from '@components/data-table/data-table.component';
import { MsfInput } from '@components/ui/forms/input/input';
import { MsfSelect } from '@components/select/select';
import { CheckboxComponent } from '@components/checkbox/checkbox.component';
import { Loading } from '@components/ui/loading/loading';
import { OrchestrationTableHeadingComponent } from '@app/pages/admin/components/orchestration-table-heading/orchestration-table-heading';
import { CopyableIdentifierComponent } from '@app/pages/admin/components/copyable-identifier/copyable-identifier';
import { orchestrationEventUrl, orchestrationProcessUrl } from '@app/pages/admin/orchestration-identity.util';
import { TextareaComponent } from '@components/textarea/textarea.component';
import {
  NotificationDeliveryDto,
  NotificationEmailVolumeDto,
  NotificationRuleDto,
  NotificationTemplateDto,
  AdminInboxItemDto,
  NotifiableEventDto,
  UserNotificationsApiService,
} from '@app/services/user-notifications-api.service';
import { AdminNotificationRulePageComponent } from '@app/pages/admin/admin-notification-rule-page/admin-notification-rule-page';
import { notificationTemplateBinding } from '@app/pages/admin/notification-template-fields.util';
import {
  COMMON_NOTIFICATION_TEMPLATE_FIELDS,
  processNotificationFieldNames,
  notificationTemplateParameterRows,
} from '@app/pages/admin/notification-template-fields.util';
import { AssignmentProcessService as AssignmentProcessApiService } from '@6grain/masofa-monolith-api-client/common/api/assignmentProcess.service';
import { AdminNotificationsCatalogQuery } from '@app/pages/admin/admin-notifications-shell/admin-notifications-catalog-query';
import { filterByCatalogQuery } from '@app/pages/admin/orchestration-catalog-search.util';
import {
  NOTIFICATION_RULE_NEW_PATH,
  notificationRuleUrl,
} from '@app/pages/admin/admin-notifications-routes.util';
import {
  notificationJournalColumns,
  notificationJournalTableConfig,
} from '@app/pages/admin/notification-journal-table.util';

type AdminTab = 'rules' | 'templates' | 'messages' | 'journal';

function ruleRecipientValues(rule: NotificationRuleDto): string[] {
  return rule.recipients
    .map(item => (item.emailAddress || item.roleName || item.entityId || '').trim())
    .filter(value => value.length > 0);
}

function sameMailbox(left: string | null | undefined, email: string): boolean {
  return (left ?? '').trim().toLowerCase() === email;
}

function ruleWithEmail(rule: NotificationRuleDto, email: string): Omit<NotificationRuleDto, 'id'> | null {
  if (rule.recipients.some(item => item.kind === 'Email' && sameMailbox(item.emailAddress, email))) {
    return null;
  }
  const channels = rule.channels.some(channel => channel.toLowerCase() === 'email')
    ? rule.channels
    : [...rule.channels, 'email'];
  return {
    ...rule,
    channels,
    recipients: [...rule.recipients, { kind: 'Email', emailAddress: email, entityId: null, roleName: null }],
  };
}

function ruleWithoutEmail(rule: NotificationRuleDto, email: string): Omit<NotificationRuleDto, 'id'> | null {
  if (!rule.recipients.some(item => item.kind === 'Email' && sameMailbox(item.emailAddress, email))) {
    return null;
  }
  return {
    ...rule,
    recipients: rule.recipients.filter(item => !(item.kind === 'Email' && sameMailbox(item.emailAddress, email))),
  };
}

@Component({
  selector: 'app-admin-notifications-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    DialogModule,
    ToastModule,
    PrimaryButton,
    SecondaryButton,
    DataTableComponent,
    MsfInput,
    MsfSelect,
    CheckboxComponent,
    Loading,
    TextareaComponent,
    OrchestrationTableHeadingComponent,
    CopyableIdentifierComponent,
    AdminNotificationRulePageComponent,
  ],
  templateUrl: './admin-notifications-page.html',
  styleUrl: './admin-notifications-page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MessageService],
})
export class AdminNotificationsPageComponent implements OnInit {
  constructor() {
    const initial = inject(ActivatedRoute).snapshot.queryParamMap.get('search') ?? '';
    if (initial) {
      inject(AdminNotificationsCatalogQuery).query.set(initial);
    }
  }

  private readonly api = inject(UserNotificationsApiService);
  private readonly processesApi = inject(AssignmentProcessApiService);
  private readonly messages = inject(MessageService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  readonly catalogQuery = inject(AdminNotificationsCatalogQuery).query;
  private readonly templateProcessCell = viewChild<TemplateRef<unknown>>('templateProcessCell');
  private readonly templateEventCell = viewChild<TemplateRef<unknown>>('templateEventCell');
  private readonly ruleProcessCell = viewChild<TemplateRef<unknown>>('ruleProcessCell');

  readonly tab = signal<AdminTab>('rules');
  readonly creatingRule = signal(false);
  readonly bulkEmailMode = signal<'add' | 'remove' | null>(null);
  readonly bulkEmailBusy = signal(false);
  bulkEmail = '';
  readonly loading = signal(true);
  readonly rules = signal<NotificationRuleDto[]>([]);
  readonly templates = signal<NotificationTemplateDto[]>([]);
  readonly journal = signal<NotificationDeliveryDto[]>([]);
  readonly emailVolume = signal<NotificationEmailVolumeDto | null>(null);
  readonly emailVolumeBars = computed(() => {
    const days = this.emailVolume()?.days ?? [];
    const peak = days.reduce((max, day) => Math.max(max, day.count), 0);
    return days.map(day => ({
      date: day.date,
      day: day.date.slice(8),
      count: day.count,
      height: peak > 0 ? Math.max(4, Math.round((day.count / peak) * 100)) : 0,
    }));
  });
  readonly inboxItems = signal<AdminInboxItemDto[]>([]);
  readonly events = signal<{ label: string; value: string }[]>([]);
  readonly notifiableEvents = signal<NotifiableEventDto[]>([]);
  readonly templateDialogVisible = signal(false);
  readonly templateProcesses = signal<{ label: string; value: string }[]>([]);
  readonly templateProcessKey = signal('');
  readonly commonTemplateParameters = notificationTemplateParameterRows(COMMON_NOTIFICATION_TEMPLATE_FIELDS);
  readonly processTemplateParameters = computed(() =>
    notificationTemplateParameterRows(processNotificationFieldNames(this.templateProcessKey())),
  );
  readonly journalDetail = signal<NotificationDeliveryDto | null>(null);
  readonly inboxDetail = signal<AdminInboxItemDto | null>(null);
  readonly editingTemplateId = signal<string | null>(null);
  templateName = '';
  templateTitleEn = '';
  templateTitleRu = '';
  templateTitleUz = '';
  templateBodyEn = '';
  templateBodyRu = '';
  templateBodyUz = '';
  templateDeepLink = '';
  templateAttachments = false;
  templatePreview = false;
  templateRetired = false;

  journalEventKey = '';
  journalOutcome = '';
  journalTestsOnly = false;
  readonly rulesEventKey = signal('');
  readonly rulesProcessKey = signal('');
  readonly rulesPriority = signal('');
  messagesEventKey = '';
  messagesUnreadOnly = false;
  messagesTestsOnly = false;

  readonly outcomeOptions = [
    { label: 'adminNotifications.outcomeAll', value: '' },
    { label: 'Sent', value: 'Sent' },
    { label: 'Failed', value: 'Failed' },
    { label: 'Skipped', value: 'Skipped' },
    { label: 'DeferredQuietHours', value: 'DeferredQuietHours' },
    { label: 'DeferredDigest', value: 'DeferredDigest' },
  ];
  readonly priorityFilterOptions = [
    { label: 'adminNotifications.outcomeAll', value: '' },
    { label: 'Low', value: 'Low' },
    { label: 'Normal', value: 'Normal' },
    { label: 'High', value: 'High' },
    { label: 'Critical', value: 'Critical' },
  ];

  readonly visibleRules = computed(() => {
    const eventKey = this.rulesEventKey();
    const processKey = this.rulesProcessKey();
    const priority = this.rulesPriority();
    const rows = this.rules()
      .filter(rule => {
        if (eventKey && rule.eventKey !== eventKey) {
          return false;
        }
        if (processKey && rule.sourceProcessDefinitionKey && rule.sourceProcessDefinitionKey !== processKey) {
          return false;
        }
        if (priority && rule.priority !== priority) {
          return false;
        }
        return true;
      })
      .map(rule => ({
        ...rule,
        processUrl: orchestrationProcessUrl(rule.sourceProcessDefinitionKey),
      }));
    return filterByCatalogQuery(
      rows,
      this.catalogQuery(),
      rule => [rule.name, rule.eventKey, rule.sourceProcessDefinitionKey, rule.priority, ...ruleRecipientValues(rule)],
    );
  });
  readonly visibleTemplates = computed(() => filterByCatalogQuery(
    this.templates().map(template => {
      const binding = notificationTemplateBinding(template.name);
      return {
        ...template,
        processKey: binding?.processKey ?? '',
        eventKey: binding?.eventKey ?? '',
        processUrl: orchestrationProcessUrl(binding?.processKey),
        eventUrl: orchestrationEventUrl(binding?.eventKey),
      };
    }),
    this.catalogQuery(),
    template => [template.name, template.deepLinkPattern, template.eventKey, template.processKey],
  ));
  readonly visibleJournal = computed(() => filterByCatalogQuery(
    this.journal(),
    this.catalogQuery(),
    row => [row.eventKey, row.channelKey, row.outcome, row.renderedTitle, row.recipientUserId, row.reason],
  ));
  readonly visibleInboxItems = computed(() => filterByCatalogQuery(
    this.inboxItems(),
    this.catalogQuery(),
    row => [row.title, row.body, row.eventKey, row.recipientUserId, row.ruleId, row.priority],
  ));
  readonly catalogEmptyMessage = computed(() => this.catalogQuery().trim()
    ? 'No catalog matches'
    : 'adminNotifications.empty');

  readonly journalEventOptions = computed(() => [
    { label: 'adminNotifications.outcomeAll', value: '' },
    ...this.events(),
  ]);

  readonly ruleProcessOptions = computed(() => [
    { label: 'adminNotifications.outcomeAll', value: '' },
    ...this.templateProcesses(),
  ]);

  readonly ruleColumns = computed<TableColumn[]>(() => [
    { field: 'name', header: 'adminNotifications.name', type: 'text' },
    { field: 'eventKey', header: 'adminNotifications.event', type: 'text' },
    {
      field: 'sourceProcessDefinitionKey',
      header: 'Process',
      type: 'custom',
      minWidth: '12rem',
      customTemplate: this.ruleProcessCell(),
    },
    {
      field: 'recipients',
      header: 'adminNotifications.recipients',
      type: 'text',
      minWidth: '14rem',
      parseFunction: (row: NotificationRuleDto) => ruleRecipientValues(row).join(', ') || '—',
    },
    { field: 'priority', header: 'adminNotifications.priority', type: 'text' },
    { field: 'isActive', header: 'adminNotifications.active', type: 'boolean' },
    { field: 'actions', header: '', type: 'actions', width: '140px' },
  ]);

  readonly templateColumns = computed<TableColumn[]>(() => [
    { field: 'name', header: 'adminNotifications.template', type: 'text' },
    {
      field: 'processKey',
      header: 'Process',
      type: 'custom',
      minWidth: '12rem',
      customTemplate: this.templateProcessCell(),
    },
    {
      field: 'eventKey',
      header: 'adminNotifications.event',
      type: 'custom',
      minWidth: '12rem',
      customTemplate: this.templateEventCell(),
    },
    { field: 'isRetired', header: 'adminNotifications.retired', type: 'boolean' },
    { field: 'actions', header: '', type: 'actions', width: '100px' },
  ]);

  readonly journalColumns = computed<TableColumn[]>(() => notificationJournalColumns());
  readonly messageColumns = computed<TableColumn[]>(() => [
    { field: 'createAt', header: 'adminNotifications.time', type: 'date', dateTime: true, minWidth: '9rem' },
    { field: 'recipientUserId', header: 'adminNotifications.recipient', type: 'text', minWidth: '9rem' },
    { field: 'eventKey', header: 'adminNotifications.event', type: 'text' },
    { field: 'title', header: 'adminNotifications.messageTitle', type: 'text', minWidth: '12rem' },
    { field: 'priority', header: 'adminNotifications.priority', type: 'text' },
    { field: 'readAt', header: 'adminNotifications.readAt', type: 'date', dateTime: true },
    { field: 'isTest', header: 'adminNotifications.testFlag', type: 'boolean' },
  ]);

  readonly ruleTableConfig = computed<TableConfig>(() => ({
    paginator: true,
    rows: 20,
    emptyMessage: this.catalogEmptyMessage(),
    actions: [
      {
        icon: 'pi pi-pencil',
        tooltip: 'adminNotifications.edit',
        command: (row: NotificationRuleDto) => this.openRule(row),
        class: 'p-button-text p-button-sm p-button-rounded',
      },
      {
        icon: 'pi pi-ban',
        tooltip: 'adminNotifications.pause',
        command: (row: NotificationRuleDto) => this.setRuleActive(row, false),
        visible: (row: NotificationRuleDto) => row.isActive,
        class: 'p-button-text p-button-sm p-button-rounded',
      },
      {
        icon: 'pi pi-play',
        tooltip: 'adminNotifications.resume',
        command: (row: NotificationRuleDto) => this.setRuleActive(row, true),
        visible: (row: NotificationRuleDto) => !row.isActive,
        class: 'p-button-text p-button-sm p-button-rounded',
      },
      {
        icon: 'pi pi-send',
        tooltip: 'adminNotifications.test',
        command: (row: NotificationRuleDto) => this.testRule(row),
        class: 'p-button-text p-button-sm p-button-rounded',
      },
      {
        icon: 'pi pi-trash',
        tooltip: 'adminNotifications.delete',
        command: (row: NotificationRuleDto) => this.deleteRule(row),
        class: 'p-button-text p-button-sm p-button-danger p-button-rounded',
      },
    ],
  }));

  readonly templateTableConfig = computed<TableConfig>(() => ({
    paginator: true,
    rows: 20,
    emptyMessage: this.catalogEmptyMessage(),
    actions: [
      {
        icon: 'pi pi-pencil',
        tooltip: 'adminNotifications.edit',
        command: (row: NotificationTemplateDto) => this.editTemplate(row),
        class: 'p-button-text p-button-sm p-button-rounded',
      },
    ],
  }));

  readonly journalTableConfig = computed<TableConfig>(() =>
    notificationJournalTableConfig(this.catalogEmptyMessage()),
  );
  readonly messageTableConfig = computed<TableConfig>(() => ({
    paginator: true,
    rows: 20,
    keepOrder: true,
    emptyMessage: this.catalogQuery().trim() ? 'No catalog matches' : 'adminNotifications.messagesEmpty',
  }));

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        startWith(null),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        const next = this.route.snapshot.data['tab'];
        if (next === 'rules' || next === 'templates' || next === 'messages' || next === 'journal') {
          this.tab.set(next);
        }
        this.creatingRule.set(this.router.url.split('?')[0].endsWith('/notifications/new'));
      });
    this.reload();
  }

  onTemplateDialogVisibleChange(visible: boolean): void {
    this.templateDialogVisible.set(visible);
  }

  onJournalDetailVisibleChange(visible: boolean): void {
    if (!visible) {
      this.journalDetail.set(null);
    }
  }

  onInboxDetailVisibleChange(visible: boolean): void {
    if (!visible) {
      this.inboxDetail.set(null);
    }
  }

  reload(): void {
    this.loading.set(true);
    this.api.getEmailVolume().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: volume => this.emailVolume.set(volume),
      error: () => this.emailVolume.set(null),
    });
    forkJoin({
      rules: this.api.getRules(),
      templates: this.api.getTemplates(),
      journal: this.api.getJournal(this.journalFilters()),
      messages: this.api.getAllInbox(this.inboxFilters()),
      events: this.api.getEvents(),
      processes: this.processesApi.assignmentsProcessesGet(),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (bundle) => {
          this.rules.set(bundle.rules);
          this.templates.set(bundle.templates);
          this.journal.set(bundle.journal);
          this.inboxItems.set(bundle.messages);
          this.notifiableEvents.set(bundle.events);
          this.events.set(bundle.events.map((item) => ({ label: item.displayName, value: item.key })));
          this.templateProcesses.set(
            (bundle.processes ?? []).map((item: { key?: string; displayName?: string }) => ({
              label: item.displayName || item.key || '',
              value: item.key || '',
            })),
          );
          this.loading.set(false);
          this.applyCreateQuery();
        },
        error: () => {
          this.loading.set(false);
          this.messages.add({ severity: 'error', summary: this.translate.instant('adminNotifications.loadFailed') });
        },
      });
  }

  applyJournalFilters(): void {
    this.api
      .getJournal(this.journalFilters())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (rows) => this.journal.set(rows),
        error: () => this.messages.add({ severity: 'error', summary: this.translate.instant('adminNotifications.loadFailed') }),
      });
  }

  applyInboxFilters(): void {
    this.api
      .getAllInbox(this.inboxFilters())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (rows) => this.inboxItems.set(rows),
        error: () => this.messages.add({ severity: 'error', summary: this.translate.instant('adminNotifications.loadFailed') }),
      });
  }

  openCreateTemplate(): void {
    this.editingTemplateId.set(null);
    this.templateName = '';
    this.templateTitleEn = '{{eventKey}}';
    this.templateTitleRu = '{{eventKey}}';
    this.templateTitleUz = '{{eventKey}}';
    this.templateBodyEn = '{{eventKey}}';
    this.templateBodyRu = '{{eventKey}}';
    this.templateBodyUz = '{{eventKey}}';
    this.templateDeepLink = '';
    this.templateAttachments = false;
    this.templatePreview = false;
    this.templateRetired = false;
    this.templateProcessKey.set('');
    this.templateDialogVisible.set(true);
  }

  editTemplate(row: NotificationTemplateDto): void {
    this.editingTemplateId.set(row.id);
    this.templateName = row.name;
    this.templateTitleEn = row.title['en-US'] ?? '';
    this.templateTitleRu = row.title['ru-RU'] ?? '';
    this.templateTitleUz = row.title['uz-Latn-UZ'] ?? '';
    this.templateBodyEn = row.body['en-US'] ?? '';
    this.templateBodyRu = row.body['ru-RU'] ?? '';
    this.templateBodyUz = row.body['uz-Latn-UZ'] ?? '';
    this.templateDeepLink = row.deepLinkPattern ?? '';
    this.templateAttachments = row.includeAttachments;
    this.templatePreview = row.includeReportPreview;
    this.templateRetired = row.isRetired;
    this.templateDialogVisible.set(true);
  }

  saveTemplate(): void {
    const body = {
      name: this.templateName,
      title: {
        'en-US': this.templateTitleEn,
        'ru-RU': this.templateTitleRu,
        'uz-Latn-UZ': this.templateTitleUz,
      },
      body: {
        'en-US': this.templateBodyEn,
        'ru-RU': this.templateBodyRu,
        'uz-Latn-UZ': this.templateBodyUz,
      },
      includeAttachments: this.templateAttachments,
      includeReportPreview: this.templatePreview,
      deepLinkPattern: this.templateDeepLink || null,
      isRetired: this.templateRetired,
    };
    const id = this.editingTemplateId();
    const request = id ? this.api.updateTemplate(id, body) : this.api.createTemplate(body);
    request.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.templateDialogVisible.set(false);
        this.reload();
      },
      error: () => this.messages.add({ severity: 'error', summary: this.translate.instant('adminNotifications.saveFailed') }),
    });
  }

  openBulkEmail(mode: 'add' | 'remove'): void {
    this.bulkEmail = '';
    this.bulkEmailMode.set(mode);
  }

  onBulkEmailVisibleChange(visible: boolean): void {
    if (!visible) {
      this.bulkEmailMode.set(null);
    }
  }

  applyBulkEmail(): void {
    const mode = this.bulkEmailMode();
    const email = this.bulkEmail.trim().toLowerCase();
    if (!mode || !email.includes('@') || /\s/.test(email)) {
      this.messages.add({ severity: 'error', summary: this.translate.instant('adminNotifications.emailInvalid') });
      return;
    }
    const updates = this.rules().flatMap((rule) => {
      const next = mode === 'add' ? ruleWithEmail(rule, email) : ruleWithoutEmail(rule, email);
      return next ? [this.api.updateRule(rule.id, next)] : [];
    });
    if (updates.length === 0) {
      this.bulkEmailMode.set(null);
      return;
    }
    this.bulkEmailBusy.set(true);
    forkJoin(updates)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.bulkEmailBusy.set(false);
          this.bulkEmailMode.set(null);
          this.reload();
        },
        error: () => {
          this.bulkEmailBusy.set(false);
          this.messages.add({ severity: 'error', summary: this.translate.instant('adminNotifications.saveFailed') });
        },
      });
  }

  openCreateRule(): void {
    void this.router.navigateByUrl(NOTIFICATION_RULE_NEW_PATH);
  }

  openRule(row: NotificationRuleDto): void {
    void this.router.navigateByUrl(notificationRuleUrl(row.id));
  }

  setRuleActive(row: NotificationRuleDto, isActive: boolean): void {
    this.api
      .updateRule(row.id, { ...row, isActive })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.reload(),
        error: () => this.messages.add({ severity: 'error', summary: this.translate.instant('adminNotifications.saveFailed') }),
      });
  }

  deleteRule(row: NotificationRuleDto): void {
    this.api
      .deleteRule(row.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.reload(),
        error: () => this.messages.add({ severity: 'error', summary: this.translate.instant('adminNotifications.saveFailed') }),
      });
  }

  testRule(row: NotificationRuleDto): void {
    if (row.recipients.length === 0) {
      this.messages.add({ severity: 'error', summary: this.translate.instant('adminNotifications.testUserRequired') });
      return;
    }
    this.api
      .testRule(row.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.reload(),
        error: () => this.messages.add({ severity: 'error', summary: this.translate.instant('adminNotifications.testFailed') }),
      });
  }

  openJournalRow(row: NotificationDeliveryDto): void {
    this.journalDetail.set(row);
  }

  openInboxRow(row: AdminInboxItemDto): void {
    this.inboxDetail.set(row);
  }

  openInboxRule(): void {
    const row = this.inboxDetail();
    if (!row?.ruleId) {
      return;
    }
    this.inboxDetail.set(null);
    void this.router.navigateByUrl(notificationRuleUrl(row.ruleId));
  }

  private journalFilters(): {
    eventKey?: string;
    outcome?: string;
    isTest?: boolean;
  } {
    return {
      eventKey: this.journalEventKey || undefined,
      outcome: this.journalOutcome || undefined,
      isTest: this.journalTestsOnly ? true : undefined,
    };
  }

  private inboxFilters(): {
    eventKey?: string;
    unreadOnly?: boolean;
    isTest?: boolean;
  } {
    return {
      eventKey: this.messagesEventKey || undefined,
      unreadOnly: this.messagesUnreadOnly ? true : undefined,
      isTest: this.messagesTestsOnly ? true : undefined,
    };
  }

  private applyCreateQuery(): void {
    if (this.tab() !== 'rules') {
      return;
    }
    const params = this.route.snapshot.queryParamMap;
    if (params.get('create') !== '1') {
      return;
    }
    const queryParams: Record<string, string> = {};
    const eventKey = params.get('eventKey')?.trim();
    if (eventKey) {
      queryParams['eventKey'] = eventKey;
    }
    const source = params.get('sourceProcessDefinitionKey')?.trim();
    if (source) {
      queryParams['sourceProcessDefinitionKey'] = source;
    }
    const priority = params.get('priority')?.trim();
    if (priority) {
      queryParams['priority'] = priority;
    }
    void this.router.navigate([NOTIFICATION_RULE_NEW_PATH], {
      queryParams: Object.keys(queryParams).length ? queryParams : undefined,
      replaceUrl: true,
    });
  }

}
